import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Check,
    X as XIcon,
    Download,
    Phone,
    Mail,
    MapPin,
    CheckCircle2,
} from "lucide-react";
import { api, apiErrorMessage, API_BASE } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import EkgLine from "@/components/EkgLine";

const STATUS_LABEL = {
    draft: "Awaiting send",
    sent: "Awaiting your response",
    viewed: "Awaiting your response",
    accepted: "Accepted — thank you!",
    rejected: "Rejected",
    expired: "This estimate has expired",
};

export default function PublicEstimate() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [actionMsg, setActionMsg] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/public/estimates/${token}`);
            setData(data.estimate);
        } catch (e) {
            setErr(apiErrorMessage(e, "We couldn't find this estimate."));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [token]);

    const act = async (path) => {
        setBusy(true);
        setErr("");
        try {
            await api.post(`/public/estimates/${token}/${path}`);
            setActionMsg(path === "accept" ? "Thanks — we'll reach out shortly to schedule." : "Your response has been recorded.");
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (err && !data) {
        return (
            <div
                className="min-h-screen flex items-center justify-center px-5 py-12"
                style={{ background: "var(--brand-cream)" }}
            >
                <div className="brand-card max-w-[440px] text-center">
                    <p className="eyebrow" style={{ justifyContent: "center" }}>
                        Estimate
                    </p>
                    <h1 className="h2 mt-2">We couldn't find that estimate</h1>
                    <p className="text-muted mt-3">{err}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center text-muted">
                Loading…
            </div>
        );
    }

    const isDecided = ["accepted", "rejected", "expired"].includes(data.status);
    const canDecide = ["sent", "viewed"].includes(data.status);

    return (
        <div
            className="min-h-screen"
            style={{ background: "var(--brand-cream)" }}
            data-testid="public-estimate"
        >
            {/* Top brand bar */}
            <div className="border-b border-[var(--brand-surface)] bg-[var(--brand-white)]">
                <div
                    className="mx-auto flex items-center justify-between flex-wrap gap-4"
                    style={{ maxWidth: 980, padding: "1.25rem 1.25rem" }}
                >
                    <div className="logo-plate">
                        <img
                            src="/brand/logo-256.png"
                            alt="CPR Paint &amp; Coatings"
                            style={{ height: 36 }}
                        />
                    </div>
                    <div className="text-right text-sm text-muted">
                        <p
                            className="font-display font-bold"
                            style={{ color: "var(--brand-dark)" }}
                        >
                            {data.company?.name}
                        </p>
                        {data.company?.phone && <p>{data.company.phone}</p>}
                        {data.company?.email && <p>{data.company.email}</p>}
                    </div>
                </div>
                <EkgLine />
            </div>

            <div
                className="mx-auto"
                style={{ maxWidth: 980, padding: "2.5rem 1.25rem 4rem" }}
            >
                <p className="eyebrow">
                    Estimate {data.estimate_number}
                </p>
                <h1 className="h1 mt-3" data-testid="pub-title">
                    Your project estimate
                </h1>
                <p className="text-muted mt-2 max-w-[600px]">
                    Prepared for <strong>{data.client?.name}</strong> · Issued{" "}
                    {fmtDate(data.created_at)} · Valid until{" "}
                    {fmtDate(data.expiration_date)}
                </p>

                {/* Status / actions banner */}
                {actionMsg ? (
                    <div
                        className="brand-card mt-6 fade-up"
                        style={{
                            background: "rgba(46,125,50,0.08)",
                            borderColor: "rgba(46,125,50,0.4)",
                            color: "var(--brand-success)",
                        }}
                        data-testid="pub-success"
                    >
                        <CheckCircle2 size={18} className="inline mr-2" />
                        {actionMsg}
                    </div>
                ) : isDecided ? (
                    <div
                        className="brand-card mt-6 fade-up"
                        style={{
                            background:
                                data.status === "accepted"
                                    ? "rgba(46,125,50,0.08)"
                                    : "var(--brand-surface)",
                            borderColor:
                                data.status === "accepted"
                                    ? "rgba(46,125,50,0.4)"
                                    : "var(--brand-surface-2)",
                        }}
                        data-testid="pub-status-banner"
                    >
                        <p className="font-display font-bold">
                            {STATUS_LABEL[data.status]}
                        </p>
                    </div>
                ) : (
                    canDecide && (
                        <div
                            className="brand-card mt-6 fade-up flex items-center justify-between gap-3 flex-wrap"
                            style={{ background: "var(--brand-white)" }}
                            data-testid="pub-actions"
                        >
                            <div>
                                <p className="font-display font-bold">
                                    Ready to move forward?
                                </p>
                                <p className="text-sm text-muted">
                                    Approve to lock in pricing and we'll reach out to schedule.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => act("accept")}
                                    disabled={busy}
                                    data-testid="pub-accept"
                                >
                                    <Check size={16} />
                                    Accept estimate
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => act("reject")}
                                    disabled={busy}
                                    data-testid="pub-reject"
                                >
                                    <XIcon size={16} />
                                    Not now
                                </button>
                            </div>
                        </div>
                    )
                )}

                {err && (
                    <div
                        className="brand-card mt-4"
                        style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                        data-testid="pub-error"
                    >
                        {err}
                    </div>
                )}

                {/* Client + Job */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Client</p>
                        <p className="font-display font-bold mt-2">{data.client?.name}</p>
                        {data.client?.email && (
                            <p className="text-sm text-muted flex items-center gap-2 mt-1">
                                <Mail size={14} />
                                {data.client.email}
                            </p>
                        )}
                        {data.client?.phone && (
                            <p className="text-sm text-muted flex items-center gap-2 mt-1">
                                <Phone size={14} />
                                {data.client.phone}
                            </p>
                        )}
                    </div>
                    <div className="brand-card fade-up delay-2">
                        <p className="eyebrow">Job address</p>
                        <p className="font-display font-bold mt-2 flex items-start gap-2">
                            <MapPin size={16} className="mt-1 shrink-0" />
                            {data.job_address}
                        </p>
                    </div>
                    <div className="brand-card fade-up delay-3">
                        <p className="eyebrow">Service</p>
                        <p className="font-display font-bold mt-2 capitalize">
                            {data.service_type}
                        </p>
                    </div>
                </div>

                {/* Line items */}
                <div className="brand-card mt-5 fade-up delay-1" data-testid="pub-items">
                    <p className="eyebrow">Scope of work</p>
                    <div className="overflow-x-auto mt-3">
                        <table className="brand-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th className="text-right">Qty</th>
                                    <th>Unit</th>
                                    <th className="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.line_items?.map((li, i) => (
                                    <tr key={i} style={{ cursor: "default" }}>
                                        <td>{li.description}</td>
                                        <td className="text-right font-mono">{li.quantity}</td>
                                        <td>{li.unit?.replace("_", " ")}</td>
                                        <td className="text-right font-mono">
                                            {money(li.total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <div className="min-w-[260px] space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted">Labor</span>
                                <span className="font-mono">{money(data.labor_total)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted">Materials</span>
                                <span className="font-mono">{money(data.materials_total)}</span>
                            </div>
                            <div
                                className="flex justify-between pt-2 border-t border-[var(--brand-surface)]"
                                style={{ fontSize: "1.5rem" }}
                            >
                                <span className="font-display font-bold">Total</span>
                                <span
                                    className="font-display font-bold font-mono"
                                    style={{ color: "var(--brand-red)" }}
                                    data-testid="pub-total"
                                >
                                    {money(data.grand_total)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {data.notes && (
                    <div className="brand-card mt-5 fade-up delay-2">
                        <p className="eyebrow">Notes</p>
                        <p className="mt-2 whitespace-pre-wrap">{data.notes}</p>
                    </div>
                )}

                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    <a
                        href={`${API_BASE}/public/estimates/${token}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-secondary"
                        data-testid="pub-pdf"
                    >
                        <Download size={14} />
                        Download as PDF
                    </a>
                </div>

                <p className="text-center text-xs text-muted mt-10">
                    {data.company?.name} · Conservation · Painting · Restoration
                </p>
            </div>
        </div>
    );
}
